import { useState } from 'react';
import AppTile from './AppTile';
import { itemLabel, itRowId, safeConsoleHref } from './checklist';
import { formatDate } from '../../../lib/formatDate';

function resolvedMeta(resolution, verifiedInOdoo) {
  if (!resolution) return null;
  const who = resolution.closed_by_name || 'IT';
  const when = formatDate(resolution.closed_at);
  if (resolution.status === 'not_applicable') return `Not applicable · ${who} · ${when}`;
  return verifiedInOdoo ? `Confirmed in Odoo · ${who} · ${when}` : `Closed by ${who} · ${when}`;
}

/**
 * One Close manually / Remove paid seat item (spec §4.3). Open items carry a
 * checkbox (Mark closed) and a Not applicable button; resolved items sit in
 * Closed with Undo. View-only holders get the row and no controls.
 * A verify-by-Odoo item (`verify === 'odoo'`) never takes a tick or Not
 * applicable: its only control is Check in Odoo, which closes it when Odoo
 * confirms the user is archived.
 */
export default function ItAppItemRow({ item, canManage, onSet, onVerify, verifying = false, stillActive = false }) {
  const [stepsOpen, setStepsOpen] = useState(false);
  const label = itemLabel(item);
  const rowId = itRowId(item);
  const boxId = `${rowId}-check`;
  const stepsId = `${rowId}-steps`;
  const resolved = !!item.resolution;
  const odoo = item.verify === 'odoo';
  const showCheck = canManage && !resolved && !odoo;
  const href = safeConsoleHref(item.admin_console_href);
  const steps = Array.isArray(item.offboarding_steps) ? item.offboarding_steps : [];
  const instruction = item.item_type === 'remove_seat' ? item.seat_instruction : item.close_action;
  const meta = resolvedMeta(item.resolution, odoo);

  return (
    <li id={rowId} className={`ca-row${showCheck ? ' ca-row--check' : ''}`}>
      <AppTile app={{ display_text: item.name }} />
      {showCheck && (
        // Same wide tap target as the Google rows' checkbox.
        <span className="ca-row__hit" onClick={(e) => { if (e.target === e.currentTarget) e.currentTarget.querySelector('input')?.click(); }}>
          <input id={boxId} type="checkbox" className="ca-row__check" checked={false}
            aria-label={`Mark ${label} closed`} onChange={() => onSet(item, 'closed')} />
        </span>
      )}
      <div className="ca-row__body">
        <span className="ca-row__name">
          {item.name}
          {item.sign_in_unconfirmed && <span className="ca-badge">Sign-in not confirmed</span>}
          {/* Only an explicit false: unlinked apps can't be checked against the person's grants. */}
          {item.catalog_linked === false && <span className="ca-badge">Not linked to the catalog</span>}
        </span>
        {instruction && <span className="ca-row__meta">{instruction}</span>}
        {item.item_type === 'remove_seat' && item.seat_note && <span className="ca-row__meta ca-row__note">{item.seat_note}</span>}
        {meta && <span className="ca-row__meta">{meta}</span>}
        {odoo && !resolved && stillActive && (
          <span className="ca-row__meta ca-note--error" role="status">Still active in Odoo. Archive the user there, then check again.</span>
        )}
        {(href || steps.length > 0) && (
          <span className="ca-row__links">
            {href && <a href={href} target="_blank" rel="noopener noreferrer">Admin console</a>}
            {steps.length > 0 && (
              <button type="button" className="ca-steps-toggle" aria-expanded={stepsOpen} aria-controls={stepsId}
                onClick={() => setStepsOpen((o) => !o)}>
                {stepsOpen ? 'Hide steps' : 'Show steps'}
              </button>
            )}
          </span>
        )}
        {stepsOpen && (
          <ol id={stepsId} className="ca-steps">
            {steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        )}
      </div>
      {canManage && (
        <div className="ca-row__action">
          {resolved && <button type="button" className="btn btn-secondary btn-small" aria-label={`Undo ${label}`} onClick={() => onSet(item, null)}>Undo</button>}
          {!resolved && odoo && (
            <button type="button" className="btn btn-secondary btn-small" aria-label="Check in Odoo that this person's user is archived"
              aria-busy={verifying} disabled={verifying} onClick={() => onVerify(item)}>
              {verifying ? 'Checking…' : 'Check in Odoo'}
            </button>
          )}
          {!resolved && !odoo && <button type="button" className="btn btn-secondary btn-small" aria-label={`${label} not applicable`} onClick={() => onSet(item, 'not_applicable')}>Not applicable</button>}
        </div>
      )}
    </li>
  );
}
